import React, { useEffect, useState } from 'react';
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '../ui/darkMode';
import { emendaStatusTone } from '../utils/emendaFormatters';

type FontSize = 'normal' | 'grande' | 'maior';
type Contraste = 'normal' | 'alto';

const FONTE_KEY = 'sigem-portal-fonte';
const CONTRASTE_KEY = 'sigem-portal-contraste';

const lerPreferencia = <T extends string>(chave: string, validos: readonly T[], padrao: T): T => {
  try {
    const valor = localStorage.getItem(chave) as T | null;
    return valor && validos.includes(valor) ? valor : padrao;
  } catch {
    return padrao;
  }
};

const gravarPreferencia = (chave: string, valor: string) => {
  try { localStorage.setItem(chave, valor); } catch { /* navegação privada */ }
};

/** Classes de tema do portal público, nos dois contrastes (e no modo escuro, no contraste normal). */
export interface TemaPortal {
  alto: boolean;
  page: string;
  card: string;
  muted: string;
  input: string;
  primary: string;
  secondary: string;
  header: string;
  headerKicker: string;
  headerText: string;
  headerButton: string;
  tableHead: string;
  rowHover: string;
  divider: string;
  link: string;
  chip: string;
  font: string;
  /** Rótulo de campo (dt, legenda de quadro). */
  label: string;
  /** Caixa interna dentro de um card (sub-bloco, item de lista). */
  inner: string;
  /** Aviso de informação incompleta. */
  aviso: string;
  /** Destaque discreto (faixa de resumo dentro de um card). */
  soft: string;
}

export const usePortalAcessibilidade = () => {
  const [fontSize, setFontSizeState] = useState<FontSize>(() => lerPreferencia(FONTE_KEY, ['normal', 'grande', 'maior'] as const, 'normal'));
  const [contraste, setContrasteState] = useState<Contraste>(() => lerPreferencia(CONTRASTE_KEY, ['normal', 'alto'] as const, 'normal'));
  const [escuro, alternarEscuro] = useDarkMode();
  const alto = contraste === 'alto';

  const setFontSize = (valor: FontSize) => { setFontSizeState(valor); gravarPreferencia(FONTE_KEY, valor); };
  const alternarContraste = () => {
    const proximo: Contraste = alto ? 'normal' : 'alto';
    setContrasteState(proximo);
    gravarPreferencia(CONTRASTE_KEY, proximo);
  };

  const secondary = alto
    ? 'border-yellow-600 text-yellow-300 hover:bg-gray-900'
    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700';

  const tema: TemaPortal = {
    alto,
    page: alto ? 'bg-black text-yellow-300' : 'bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100',
    card: alto ? 'border border-yellow-600 bg-gray-950' : 'border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900',
    muted: alto ? 'text-yellow-500' : 'text-slate-600 dark:text-slate-400',
    input: alto
      ? 'w-full rounded-md border border-yellow-600 bg-gray-900 px-3 py-2 text-yellow-200 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400'
      : 'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500',
    primary: alto ? 'bg-yellow-400 text-black hover:bg-yellow-300' : 'bg-blue-700 text-white hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500',
    secondary,
    header: alto ? 'border-b-2 border-yellow-400 bg-gray-950' : 'bg-blue-800 text-white dark:bg-slate-900 dark:border-b dark:border-slate-800',
    headerKicker: alto ? 'text-yellow-500' : 'text-blue-200 dark:text-blue-300',
    headerText: alto ? 'text-yellow-400' : 'text-blue-100 dark:text-slate-300',
    headerButton: alto ? secondary : 'border-white text-white hover:bg-blue-700 dark:border-slate-500 dark:hover:bg-slate-800',
    tableHead: alto ? 'bg-gray-900 text-yellow-300' : 'bg-slate-50 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
    rowHover: alto ? 'hover:bg-gray-900' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50',
    divider: alto ? 'border-yellow-800 divide-gray-800' : 'border-slate-200 divide-slate-100 dark:border-slate-800 dark:divide-slate-800',
    link: alto ? 'text-yellow-300 hover:text-yellow-100' : 'text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200',
    chip: alto ? 'bg-gray-900 text-yellow-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    font: fontSize === 'maior' ? 'text-xl' : fontSize === 'grande' ? 'text-lg' : 'text-base',
    label: alto ? 'text-yellow-500' : 'text-slate-500 dark:text-slate-400',
    inner: alto ? 'border border-yellow-800' : 'border border-slate-200 dark:border-slate-700',
    aviso: alto
      ? 'border border-yellow-400 text-yellow-200'
      : 'border border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
    soft: alto ? 'bg-gray-900' : 'bg-slate-50 dark:bg-slate-800/50',
  };

  const barra = (
    <div className={alto ? 'border-b border-yellow-800 bg-gray-950' : 'border-b border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-900'}>
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-4 py-1.5 sm:px-6 lg:px-8">
        <span className={`text-xs font-medium ${tema.muted}`}>Acessibilidade:</span>
        {(['normal', 'grande', 'maior'] as FontSize[]).map((size, index) => (
          <button
            key={size}
            type="button"
            onClick={() => setFontSize(size)}
            aria-pressed={fontSize === size}
            aria-label={['Fonte normal', 'Fonte grande', 'Fonte maior'][index]}
            className={`rounded border px-2 py-0.5 text-xs ${fontSize === size ? tema.primary : tema.secondary}`}
          >
            {index === 0 ? 'A' : `A${'+'.repeat(index)}`}
          </button>
        ))}
        <button
          type="button"
          onClick={alternarContraste}
          aria-pressed={alto}
          className={`ml-2 rounded border px-2 py-0.5 text-xs ${alto ? tema.primary : tema.secondary}`}
        >
          Alto contraste
        </button>
        {!alto && (
          <button
            type="button"
            onClick={alternarEscuro}
            aria-pressed={escuro}
            className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs ${tema.secondary}`}
          >
            {escuro ? <SunIcon className="h-3.5 w-3.5" aria-hidden="true" /> : <MoonIcon className="h-3.5 w-3.5" aria-hidden="true" />}
            Modo escuro
          </button>
        )}
      </div>
    </div>
  );

  return { tema, barra };
};

export const PortalAcessibilidadeShell: React.FC<{ tema: TemaPortal; barra: React.ReactNode; children: React.ReactNode }> = ({ tema, barra, children }) => {
  // O alto contraste tem paleta própria: desliga o escuro enquanto ativo para os componentes
  // compartilhados (paginação) não misturarem as duas.
  useEffect(() => {
    if (!tema.alto) return undefined;
    const tinhaEscuro = document.documentElement.classList.contains('dark');
    document.documentElement.classList.remove('dark');
    return () => { if (tinhaEscuro) document.documentElement.classList.add('dark'); };
  }, [tema.alto]);

  return (
    <div className={`flex min-h-screen flex-col ${tema.page} ${tema.font}`}>
      <a href="#conteudo-principal" className="sr-only z-50 rounded bg-white px-4 py-2 text-blue-900 focus:not-sr-only focus:fixed focus:left-4 focus:top-4">
        Ir para o conteúdo principal
      </a>
      {barra}
      {children}
      <footer className={`mt-auto border-t ${tema.alto ? 'border-yellow-800 bg-gray-950' : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'}`}>
        <div className={`mx-auto max-w-7xl px-4 py-4 text-center text-xs ${tema.muted}`}>
          Portal da Transparência Municipal · Emendas Parlamentares · dados abertos em CSV e JSON na consulta pública
        </div>
      </footer>
    </div>
  );
};

/** Situação da emenda como etiqueta: texto sempre visível, a cor só reforça. */
export const SituacaoBadge: React.FC<{ status?: string; alto?: boolean }> = ({ status, alto }) => (
  <span className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${alto ? 'border-yellow-500 text-yellow-300' : emendaStatusTone(status)}`}>
    {status || 'Não informada'}
  </span>
);
